/**
 * The single wire envelope for every real-time message (protocol.md §5).
 *
 * These types are hand-written to match `TeamSim.Contracts` and the on-the-wire camelCase JSON
 * exactly. Cross-checked against:
 *   - backend/src/TeamSim.Contracts/Envelope.cs   (envelope shape, camelCase, WhenWritingNull)
 *   - backend/src/TeamSim.Contracts/WireTypes.cs   (the `type` tokens)
 *
 * FUTURE: generate this module from the Contracts JSON Schemas in CI (web-client.md §1/§10) so a
 * backend contract change fails the build instead of drifting silently. For S0 these are authored
 * by hand and kept in one file.
 */

/** Protocol version this client speaks. Negotiated at `sys.hello` (protocol.md §11). */
export const PROTOCOL_VERSION = 1;

/**
 * The wire envelope (protocol.md §5). Field presence mirrors the backend's `WhenWritingNull` policy:
 * absent fields are simply `undefined` after decode.
 *
 * Trust rules (protocol.md §5): `actor`, `seq`, `ts` are server-authoritative on events. A
 * client-sent `actor`/`seq` is ignored by the server, so we never set them on commands.
 */
export interface Envelope<P = unknown> {
  /** Type taxonomy: `cmd.*` | `evt.*` | `sys.*` | `stream.*`. */
  type: string;
  /** Protocol version of this message. */
  v: number;
  /** Session id; absent for connection-scoped `sys.*` frames. */
  session?: string;
  /** Monotonic per-session sequence number. SERVER events only. */
  seq?: number;
  /** Client-generated idempotency + ack-correlation id. CLIENT commands only (UUIDv7). */
  cmdId?: string;
  /** Server-stamped actor (`playerId` | `npc:key` | `system`) on events. */
  actor?: string;
  /** Server time on events; a client may send a diagnostic `clientTs` on commands. */
  ts?: string;
  /** Type-specific body. */
  payload: P;
}

/** The `type` tokens on the socket (mirror of TeamSim.Contracts/WireTypes.cs). */
export const WireTypes = {
  // Control plane (sys.*), unsequenced.
  Hello: 'sys.hello',
  Welcome: 'sys.welcome',
  Live: 'sys.live',
  Ack: 'sys.ack',
  Error: 'sys.error',
  Ping: 'sys.ping',
  Pong: 'sys.pong',
  Reauth: 'sys.reauth',
  Drain: 'sys.drain',
  Notice: 'sys.notice',

  // Commands (cmd.*), client -> server.
  ChatSend: 'cmd.chat.send',
  /**
   * React to a message by its committed seq (workspace-client.md §2i). The backend handles this token;
   * NO client sends one yet - the reaction affordance is the workspace's build step 7. Kept in the
   * mirror because this file's job is to be a complete mirror, not a list of what is wired.
   */
  ChatReact: 'cmd.chat.react',
  SessionLeave: 'cmd.session.leave',
  /** A gameplay input applied to the session's engine (the S1 bridge). */
  GameAction: 'cmd.game.action',
  // Client-side self-heal command (protocol.md §7). NOTE: the S0 backend does not yet handle this
  // token (only cmd.chat.send / cmd.session.leave are dispatched); on drift the client falls back
  // to a resume-reconnect. Kept here so the intent is typed for when the backend adds it.
  SessionSync: 'cmd.session.sync',

  // Events (evt.*), server -> clients, committed + sequenced (except presence, see below).
  ChatMessage: 'evt.chat.message',
  /** A committed reaction to a message (§2i). Expressive and cited by NPC memory; never moves a meter. */
  ChatReactionEvent: 'evt.chat.reaction',
  PresenceChanged: 'evt.presence.changed',
  SessionCreated: 'evt.session.created',
  MemberJoined: 'evt.session.memberJoined',
  MemberLeft: 'evt.session.memberLeft',
  /** A committed gameplay input + what the engine made of it (the action feed). */
  GameActionEvent: 'evt.game.action',
  GameNarrationEvent: 'evt.game.narration',
  /**
   * The session's retro, with its prose (S2.5). Committed rather than streamed because the retro is the
   * one thing a team comes back to read: it replays onto a client that reconnects after the end.
   */
  RetroPublishedEvent: 'evt.retro.published',
  /**
   * The session crossed a rung of the LLM budget ladder (S2.6). Committed rather than streamed: a team
   * that reconnects should still be able to see that the coaching stopped, and why.
   */
  FidelityChangedEvent: 'evt.game.fidelityChanged',
  /** An NPC said something about a beat that is already committed (S2.3). */
  NpcSaidEvent: 'evt.npc.said',
  /**
   * A scene beat was read out loud (S2.4) - the standup digest, for now. Scene-agnostic: the payload
   * names its scene and beat, so the next scene to get a voice is not a second event type.
   */
  SceneNarratedEvent: 'evt.scene.narrated',
  /**
   * A compiled policy card was previewed to the table (S2.4 §4.2 step 4). Committed rather than
   * request/response: the people who will be asked to adopt a standing rule have to be able to read it
   * first, and a player who reconnects has to still find it.
   */
  PolicyPreviewedEvent: 'evt.policy.previewed',
  /**
   * The team reached a milestone, with the engine's own reflection over the stretch behind it (DEC-17 M1).
   * Committed rather than streamed because a chapter has to survive a reconnect: a player who drops out
   * mid-session and comes back should still be able to read what the team crossed while they were gone.
   */
  MilestoneReachedEvent: 'evt.milestone.reached',
  /**
   * The milestone note, written up (DEC-17 M1). Separate from the crossing for the same reason narration
   * is separate from the action it describes: the gate commits at engine speed and the words arrive a
   * moment later, or never, in which case the engine's own account of the span is what the team reads.
   */
  MilestoneNarratedEvent: 'evt.milestone.narrated',

  // Stream (stream.*), server -> clients, ephemeral + unsequenced.
  /** The per-seat-projected engine state (the S1 bridge). */
  StateDelta: 'stream.state.delta',
  /**
   * The retro document broadcast the moment the session ends, carrying the engine-computed package and no
   * prose yet — the story is a background job, and it arrives on `evt.retro.published` a moment later.
   */
  Retro: 'stream.retro',

  // The streaming sub-protocol (protocol.md §8). Ephemeral: never persisted, never resumed, never
  // sequenced. A reconnect mid-stream simply misses the rest and renders the committed event instead.
  /** A stream is starting: `{ streamId, kind, session, scope }`. */
  StreamOpen: 'stream.open',
  /** One fragment: `{ streamId, idx, payload: { text } }`. */
  StreamChunk: 'stream.chunk',
  /** The end: `{ streamId, status, committedSeq }`. `aborted` means the committed event wins. */
  StreamEnd: 'stream.end',
  /**
   * A character is composing: `{ seat, typing }`. Ephemeral like every other stream frame.
   *
   * Latency is CHARACTERIZATION in a messenger (workspace-client.md §2j): four seconds of a careful NPC
   * visibly typing reads as personality, where four seconds of nothing reads as a hung app. The clear
   * is the same frame with the flag down, so a client that missed the raise still has one handler.
   */
  NpcTyping: 'stream.npc.typing',
} as const;

/** True for a client -> server intent (protocol.md §5.1: every `cmd.*` is acked). */
export function isCommandType(type: string): boolean {
  return type.startsWith('cmd.');
}

/** True for a server -> clients committed event. */
export function isEventType(type: string): boolean {
  return type.startsWith('evt.');
}

/** Presence states broadcast on `evt.presence.changed` (protocol.md §9). */
export type PresenceState = 'online' | 'away' | 'offline';
